'use client';

import { ArrowDown } from 'lucide-react';
import { Button } from '@/app/_components/core/button/button';
import { ComponentSection } from '@/app/_components/core/component-section';
import { Copytext } from '@/app/_components/core/copytext';
import { Headline } from '@/app/_components/core/headline';
import { RespImage } from '@/app/_components/core/resp-image/resp-image';
import { RichText } from '@/app/_components/core/rich-text';
import { Separator } from '@/app/_components/core/separator/separator';
import FormEmbedComponent from '@/app/_components/shared/form-embed/form-embed';
import { getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { useLocale } from '@/app/_contexts/locale-context';
import { formatEventDateTime } from '@/app/_lib/date-utils';
import { emptyToUndefined } from '@/app/_lib/utils';
import type { EventDetailComponentProps } from './event-detail.types';

// =============================================================================
// Constants
// =============================================================================

const REGISTRATION_ANCHOR_ID = 'event-registration';

// =============================================================================
// Helpers
// =============================================================================

function scrollToRegistration() {
  document
    .getElementById(REGISTRATION_ANCHOR_ID)
    ?.scrollIntoView({ behavior: 'smooth' });
}

// =============================================================================
// Component
// =============================================================================

function EventDetailComponent({
  component,
  componentPosition,
  isNested,
}: EventDetailComponentProps) {
  const { event } = component;
  const locale = useLocale();
  const trackingPrefix = getTrackingPrefix(component);

  // Labels from globalLabels
  const titleLabel = useLabel('title', 'Titel');
  const dateTimeLabel = useLabel('dateTime', 'Datum & Uhrzeit');
  const locationLabel = useLabel('location', 'Location');
  const atLabel = useLabel('dateTimeFrom', 'ab');
  const oclockLabel = useLabel('dateTimeOClock', 'Uhr');
  const languageLabel = useLabel('eventLanguage', 'Sprache');
  const toRegistrationLabel = useLabel('toRegistration', 'Go to registration');

  return (
    <>
      <ComponentSection isNested={isNested}
        {...getSectionProps(component, componentPosition, isNested)}
        className="mx-auto max-w-3xl py-section-padding-y-mobile xl:py-section-padding-y-desktop"
      >
        <div className="container">
          {/* Hero Image */}
          {event.image?.srcsetCandidates &&
            !Array.isArray(event.image.srcsetCandidates) &&
            event.image.srcsetCandidates.landscape && (
              <RespImage
                srcsetCandidates={event.image.srcsetCandidates.landscape}
                alt={emptyToUndefined(event.image.altText) ?? event.title}
                title={event.image.title ?? undefined}
                sizes="(min-width: 768px) 736px, (min-width: 640px) 608px, calc(100vw - 32px)"
                pictureClassName="block aspect-video overflow-hidden rounded-event-detail-media-radius"
                className="size-full"
                priority
              />
            )}

          {/* Title + Description */}
          <div className="mt-16 flex flex-col gap-6">
            <Headline as="h1">{event.title}</Headline>
            {event.description && (
              <Copytext variant="lead">{event.description}</Copytext>
            )}
          </div>

          <Separator spacing="lg" />

          {/* CTA Button */}
          {event.embedCode && (
            <Button
              className="cursor-pointer"
              endIcon={<ArrowDown />}
              onClick={scrollToRegistration}
              data-track-id={
                trackingPrefix ? `${trackingPrefix}-registration` : undefined
              }
            >
              {toRegistrationLabel}
            </Button>
          )}

          {/* Event Metadata */}
          <div className="mt-10 space-y-2">
            <dl>
              <Copytext as="dt" className="mb-0 font-bold lg:font-bold lg:inline">
                {titleLabel}:{' '}
              </Copytext>
              <Copytext as="dd" className="mb-0 lg:inline">
                {event.title}
              </Copytext>
            </dl>
            <dl>
              <Copytext as="dt" className="mb-0 font-bold lg:font-bold lg:inline">
                {dateTimeLabel}:{' '}
              </Copytext>
              <Copytext as="dd" className="mb-0 lg:inline">
                {formatEventDateTime(event.startDateTime, event.endDateTime, {
                  at: atLabel,
                  oclock: oclockLabel,
                }, locale)}
              </Copytext>
            </dl>
            {event.location && (
              <dl>
                <Copytext as="dt" className="mb-0 font-bold lg:font-bold lg:inline">
                  {locationLabel}:{' '}
                </Copytext>
                <Copytext as="dd" className="mb-0 lg:inline">
                  {event.location}
                </Copytext>
              </dl>
            )}
            {event.language && (
              <dl>
                <Copytext as="dt" className="mb-0 font-bold lg:font-bold lg:inline">
                  {languageLabel}:{' '}
                </Copytext>
                <Copytext as="dd" className="mb-0 lg:inline">
                  {event.language}
                </Copytext>
              </dl>
            )}
          </div>

          {/* Detail Description */}
          {event.detailDescription && (
            <RichText className="mt-10 max-w-none">
              {event.detailDescription}
            </RichText>
          )}
        </div>
      </ComponentSection>

      {/* Registration Form */}
      {event.embedCode && (
        <FormEmbedComponent
          component={{
            type: 'form-embed',
            sectionId: REGISTRATION_ANCHOR_ID,
            embedIntro: event.embedIntro,
            html: event.embedCode,
            theme: 'theme0',
            layoutType: '1col',
            mediaType: 'none',
          }}
          eventMeta={{
            title: event.title,
            dateTime: formatEventDateTime(event.startDateTime, event.endDateTime, {
              at: atLabel,
              oclock: oclockLabel,
            }, locale),
            dateTimeLabel,
            location: event.location,
            locationLabel,
          }}
        />
      )}
    </>
  );
}

export { EventDetailComponent };
export default EventDetailComponent;
